import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { twistNow } from "../../../engine/bench";
import { inverse } from "../../../engine/state";
import type { TournamentEvent, TournamentState } from "../../../engine/types";
import { countdown, waitingFor } from "../../../shared/day";
import { useLook } from "../lib/look";
import { useTournament, type Screen, type Tournament } from "../lib/useTournament";
import { STATUS_WORDS } from "../lib/transport";
import "./Admin.css";
import { OPENED_EVENT, betweenRounds, mmss, phaseLabel, remaining, useTick } from "./admin/common";
import { DayTab } from "./admin/DayTab";
import { EventsTab } from "./admin/EventsTab";
import { AdminLock } from "./admin/Lock";
import { MoreTab } from "./admin/MoreTab";
import { NoteKey } from "./admin/Note";
import { addressAfterNewLink, adminToken } from "./admin/newLink";
import { PlayersTab } from "./admin/PlayersTab";
import { RoundsTab } from "./admin/RoundsTab";
import { ScoresTab } from "./admin/ScoresTab";
import { changes, twistStarted } from "./admin/toasts";
import { momentAfter, movedOn, seen, type UndoMoment } from "./admin/undo";
import { readName, saveName } from "./admin/who";
import { WhoCard } from "./admin/WhoCard";
import { sentence } from "./admin/words";

const TABS = [
  { id: "day", label: "Day" },
  { id: "rounds", label: "Rounds" },
  { id: "players", label: "Players" },
  { id: "scores", label: "Scores" },
  { id: "events", label: "Events" },
  { id: "more", label: "More" },
] as const;

type TabId = (typeof TABS)[number]["id"];

/** How long the way back is offered after an action. */
const UNDO_MS = 8000;
/** Sends closer together than this are one tap doing many things ("Everyone here"): no single way back. */
const BURST_MS = 100;
/** A change that lands this soon after an action with an undo bar is that action coming back from the server. */
const ECHO_MS = 1500;
/**
 * What the undo bar and a toast must never sit on: a confirm that is asking, the lunch form, and a
 * QR code that is open (one made for someone to scan straight away above all).
 */
const OPENED = ".ad-confirm, .ad-lunch-form, .ad-link-open";
/** What they must not cover when they are at the top. */
const CLOCK = ".ad-clock";
/** Anything that can be tapped. */
const KEY = "button, a, input, select, textarea, label, [role=button]";
/** What the server says when this phone's own link has been replaced or taken away. */
const STOPPED = /has been replaced|was taken away/;

/**
 * Where the floats are: above the tab bar or over the top line, and whether the toast is put away
 * to make room. `under` is set when the page is scrolled so that the clock is where the top line
 * was: the floats then start that far down the glass, just under the clock.
 */
interface Spot {
  up: boolean;
  solo: boolean;
  under: number | null;
}

const DOWN: Spot = { up: false, solo: false, under: null };

/** How much of two boxes is shared, 0 when they are clear of each other. */
function shared(a: DOMRect, b: DOMRect): number {
  const w = Math.min(a.right, b.right) - Math.max(a.left, b.left);
  const h = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
  return w > 0 && h > 0 ? w * h : 0;
}

interface Undo {
  /** The id the action went out with, so a refusal of it takes the bar away. */
  id: string;
  text: string;
  back: TournamentEvent;
  /** The day as the action left it: when it moves on, the bar goes. */
  moment: UndoMoment;
}

/**
 * A link's page on this same site: the players' side of the tournament this admin screen is on.
 * The server writes links with the address it was last reached at, which is not always this one.
 */
function samePlace(link: string): string {
  try {
    const u = new URL(link, location.origin);
    return `${u.pathname}${u.search}`;
  } catch {
    return link;
  }
}

function tabFromHash(): TabId {
  const h = location.hash.slice(1);
  // no tab asked for: the one the day is run from
  return TABS.find((x) => x.id === h)?.id ?? "rounds";
}

/**
 * The admin's phone: six tabs along the bottom, every control the day needs. Each tab puts what
 * is used during the day first and keeps the rest in folds, so nothing needed in a hurry is more
 * than a short scroll away.
 */
export function AdminView({ screen }: { screen: Screen }) {
  // The owner's link asks who is holding the phone; a named admin's link brings its own name
  // (decision 38), which the server puts on their actions whatever this phone says.
  const [typed, setTyped] = useState<string | null>(readName);
  const [given, setGiven] = useState<string | null>(null);
  const signed = given ?? typed;
  const t = useTournament(screen, signed ? `admin:${signed}` : undefined);
  const named = t.identity?.role === "admin" && t.identity.owner === false;
  // until this phone has typed its admin PIN the screen shows only the lock (decision 40)
  const locked = t.identity?.role === "admin" && t.identity.locked === true;
  const name = named ? t.identity?.name ?? null : typed;
  useEffect(() => setGiven(named ? t.identity?.name ?? null : null), [named, t.identity?.name]);
  const s = t.state;
  const [tab, setTab] = useState<TabId>(tabFromHash);
  const [toast, setToast] = useState<string | null>(null);
  const [stopped, setStopped] = useState<string | null>(null);
  const [undo, setUndo] = useState<Undo | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const undoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSend = useRef(0);
  const seenRefusal = useRef(t.refusal?.at ?? 0);
  const prevState = useRef<TournamentState | null>(s);
  // The undo bar and the toast sit above the tab bar. They move to the top line of the screen
  // whenever they would sit on the key that was just tapped, a confirm that is asking or the
  // lunch form, and they never cover the clock. With no room for both, the toast is put away.
  const [spot, setSpot] = useState<Spot>(DOWN);
  const spotNow = useRef(spot);
  spotNow.current = spot;
  const floats = useRef<HTMLDivElement | null>(null);
  // the key that was tapped last, and where it was then: it may be gone by the time the bar comes
  const tapped = useRef<{ key: Element; box: DOMRect; scrollY: number } | null>(null);
  const place = useCallback(() => {
    const el = floats.current;
    const cur = spotNow.current;
    if (!el || el.childElementCount === 0) {
      if (cur.up || cur.solo || cur.under !== null) setSpot(DOWN);
      return;
    }
    const keys: DOMRect[] = [];
    const tap = tapped.current;
    if (tap) {
      const gone = window.scrollY - tap.scrollY;
      keys.push(tap.key.isConnected ? tap.key.getBoundingClientRect() : new DOMRect(tap.box.left, tap.box.top - gone, tap.box.width, tap.box.height));
    }
    for (const opened of document.querySelectorAll(OPENED)) {
      // from its top to the bottom of its last key: a line of small print under the keys may be covered;
      // with no keys in it (a code to scan) the whole of it
      const box = opened.getBoundingClientRect();
      const inside = [...opened.querySelectorAll(KEY)];
      const last = inside.length ? inside.reduce((y, k) => Math.max(y, k.getBoundingClientRect().bottom), box.top) : box.bottom;
      keys.push(new DOMRect(box.left, box.top, box.width, Math.min(box.bottom, last) - box.top));
    }
    const clock = document.querySelector(CLOCK)?.getBoundingClientRect();
    // where they are now first, so they only move when they have to
    const tries: Spot[] = [{ up: cur.up, solo: false, under: null }, { up: !cur.up, solo: false, under: null }];
    if (el.childElementCount > 1) tries.push({ up: cur.up, solo: true, under: null }, { up: !cur.up, solo: true, under: null });
    let best = tries[0] as Spot;
    let least = Infinity;
    for (const spot of tries) {
      el.classList.toggle("up", spot.up);
      el.classList.toggle("solo", spot.solo);
      el.style.top = "";
      let box = el.getBoundingClientRect();
      let onClock = spot.up && clock ? shared(box, clock) : 0;
      if (onClock > 0 && clock && window.scrollY > 0) {
        // the clock has been scrolled up to the top line: start under it, as far from it as from the top edge
        spot.under = Math.round(clock.bottom + box.top);
        el.style.top = `${spot.under}px`;
        box = el.getBoundingClientRect();
        onClock = 0;
      }
      // with no place clear of everything, the keys count for more than the clock
      const hit = keys.reduce((n, k) => n + shared(box, k), 0) * 1000 + onClock;
      if (hit < least) {
        best = spot;
        least = hit;
      }
      if (hit === 0) break;
    }
    el.classList.toggle("up", cur.up);
    el.classList.toggle("solo", cur.solo);
    el.style.top = cur.up && cur.under !== null ? `${cur.under}px` : "";
    if (best.up !== cur.up || best.solo !== cur.solo || best.under !== cur.under) setSpot(best);
  }, []);
  useEffect(() => {
    let frame = 0;
    const soon = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(place);
    };
    const down = (e: PointerEvent) => {
      const at = e.target instanceof Element ? e.target : null;
      if (!at || at.closest(".ad-floats")) return; // a tap on the bar itself moves nothing
      const key = at.closest(KEY);
      if (key) tapped.current = { key, box: key.getBoundingClientRect(), scrollY: window.scrollY };
      place();
    };
    window.addEventListener("pointerdown", down, true);
    window.addEventListener("click", soon, true); // what the tap opened is on the page a frame later
    window.addEventListener(OPENED_EVENT, place);
    window.addEventListener("scroll", place, { passive: true });
    window.addEventListener("resize", soon);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("pointerdown", down, true);
      window.removeEventListener("click", soon, true);
      window.removeEventListener(OPENED_EVENT, place);
      window.removeEventListener("scroll", place);
      window.removeEventListener("resize", soon);
    };
  }, [place]);
  // and after every draw of this screen (twice a second at least), before it is painted
  useLayoutEffect(place);
  useTick(500);
  useLook(s?.config.look);

  const say = useCallback((text: string) => {
    setToast(text);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(null), 4000);
  }, []);

  function offer(next: Undo | null) {
    if (undoTimer.current) clearTimeout(undoTimer.current);
    undoTimer.current = next ? setTimeout(() => setUndo(null), UNDO_MS) : null;
    setUndo(next);
  }

  /**
   * Every tab sends through here: the action goes out under this admin's name, and when it has a
   * way back (worked out from the state before it) the undo bar offers it. One level: the newest
   * action replaces the last one's bar.
   */
  function send(event: TournamentEvent, opts?: { queue?: boolean; id?: string }): string {
    if (!name) {
      say("Say who you are first, at the top of the screen.");
      window.scrollTo(0, 0);
      return "";
    }
    const back = s ? inverse(s, event) : null;
    const text = s ? sentence(s, event) : "";
    const id = t.send(event, opts);
    const burst = Date.now() - lastSend.current < BURST_MS;
    lastSend.current = Date.now();
    offer(s && back && !burst ? { id, text, back, moment: momentAfter(s, event) } : null);
    return id;
  }

  function takeBack() {
    if (!undo) return;
    t.send(undo.back); // straight out: an undo has no undo
    offer(null);
    say("Undone.");
  }

  const act: Tournament = { ...t, send };

  // admin actions are not queued: a refusal comes straight back, in words written for people
  useEffect(() => {
    if (t.refusal && t.refusal.at !== seenRefusal.current) {
      seenRefusal.current = t.refusal.at;
      // this phone's link has stopped: say so for good, not in a toast that goes (the first words
      // win: the server's, said as it happened, over the ones a later check works out)
      if (!t.refusal.id && STOPPED.test(t.refusal.reason)) {
        const reason = t.refusal.reason;
        setStopped((was) => was ?? reason);
        offer(null);
        setToast(null);
        window.scrollTo(0, 0);
        return;
      }
      say(t.refusal.reason);
      if (undo && undo.id === t.refusal.id) offer(null);
    }
  }, [t.refusal, say]);

  // the round ended or started, or the hall was paused or resumed by someone else: the way back
  // was worked out for a day that has moved on, so it is not offered any more
  useEffect(() => {
    if (!undo || !s) return;
    const moment = seen(undo.moment, s);
    if (movedOn(moment, s)) offer(null);
    else if (moment !== undo.moment) setUndo({ ...undo, moment });
  }, [s, undo?.id]);

  // arrivals, breaks, leavers, rounds closing and starting: one toast, the newest wins
  useEffect(() => {
    const prev = prevState.current;
    prevState.current = s;
    if (!prev || !s || s === prev || s.seq < prev.seq) return; // first sight or a reset: nothing to tell
    const text = changes(prev, s);
    // what this admin has just done is already in the undo bar: no need to say it twice
    const mine = undo !== null && Date.now() - lastSend.current < ECHO_MS;
    if (text && !mine) say(text);
  }, [s, say]);

  // a twist starts by the clock, not by an event: say so when it does (not on first sight)
  const twist = s ? twistNow(s, t.now()) : null;
  const twistOn = twist?.on ? twist.eventId : null;
  const seenTwist = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (!s) return;
    const seen = seenTwist.current;
    seenTwist.current = twistOn;
    if (seen !== undefined && twist && twistOn && twistOn !== seen) say(twistStarted(twist.name, twist.endsAt - twist.startsAt));
  }, [twistOn, !s]);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
    if (undoTimer.current) clearTimeout(undoTimer.current);
  }, []);

  // the server says a name has been locked after wrong PINs, or the hidden way in was paused
  const pinNews = t.pinNews;
  const seenNews = useRef(pinNews?.at ?? 0);
  useEffect(() => {
    if (!pinNews || pinNews.at === seenNews.current) return;
    seenNews.current = pinNews.at;
    say(pinNews.text);
  }, [pinNews?.at, say]);

  // This phone made a new admin link: the old one in its address is dead at the next reconnect,
  // so move to the same page under the new token. Only the phone that made it is sent the link.
  const adminLink = t.links?.admin ?? "";
  useEffect(() => {
    if (screen.mock || !adminLink) return;
    const next = addressAfterNewLink(location, screen.token, adminLink);
    if (!next) return;
    // this phone's admin session goes with it, so the new link does not ask for the PIN again
    const token = adminToken(adminLink);
    if (token) t.moveAdminLink(token);
    location.replace(next);
  }, [adminLink, screen.mock, screen.token]);

  function pick(id: TabId) {
    setTab(id);
    window.history.replaceState(null, "", `#${id}`);
    window.scrollTo(0, 0);
  }

  function iAm(next: string) {
    saveName(next);
    setTyped(next);
  }

  const left = s ? remaining(s, t.now()) : null;
  const waiting = s ? waitingFor(s, t.now()) : null;
  const waitingWords = waiting?.why === "switch"
    ? ", rounds wait for you"
    : waiting?.why === "scores"
      ? `, waiting for a score from round ${waiting.roundNo}`
      : waiting?.why === "soon"
        ? `, round ${waiting.roundNo} starts in ${countdown(waiting.startsAt - t.now())}`
        : "";
  const tabProps = s ? { t: act, state: s, say, me: { name, set: iAm, named } } : null;

  return (
    <div className="close ad">
      <header className="ad-head">
        <p className="ad-summary">
          {s && !stopped && !locked ? (
            <>
              {s.clock.roundNo !== null && !betweenRounds(s) ? `Round ${s.clock.roundNo}, ${phaseLabel(s).toLowerCase()}` : phaseLabel(s)}
              {left !== null ? `, ${mmss(left)} left` : ""}
              {s.pausedAll ? ", paused" : ""}
              {waitingWords}
            </>
          ) : "Admin"}
        </p>
        {/* this phone's own player side (decision 40): the way back is three taps on its top bar */}
        {!locked && !stopped && t.links?.players && (
          <button type="button" className="ad-small ad-mygame" onClick={() => location.assign(samePlace(t.links?.players ?? ""))}>
            My game
          </button>
        )}
        {/* the owner's Note key (v26.10.027): a temporary test aid, never on a named admin's phone */}
        {!locked && !stopped && t.identity?.role === "admin" && t.identity.owner === true && (
          <NoteKey t={t} tab={TABS.find((x) => x.id === tab)?.label ?? tab} say={say} />
        )}
        <span className={"status-pill " + t.status}>{STATUS_WORDS[t.status]}</span>
      </header>

      {/* a phone whose link has stopped shows only that: no stale day to tap at, no tabs */}
      {stopped ? (
        <section className="ad-card ad-who">
          <h2>This link has stopped</h2>
          <p className="ad-note">{stopped}</p>
          <p className="ad-note">Nothing on this phone can change the day now. Open the new link when you have it.</p>
        </section>
      ) : locked ? (
        <AdminLock t={t} />
      ) : t.identity && !named && !name ? (
        <WhoCard onDone={iAm} />
      ) : null}

      {stopped || locked ? null : !tabProps ? (
        <p className="ad-note">Connecting to the tournament.</p>
      ) : tab === "day" ? (
        <DayTab {...tabProps} />
      ) : tab === "rounds" ? (
        <RoundsTab {...tabProps} />
      ) : tab === "players" ? (
        <PlayersTab {...tabProps} />
      ) : tab === "scores" ? (
        <ScoresTab {...tabProps} />
      ) : tab === "events" ? (
        <EventsTab {...tabProps} />
      ) : (
        <MoreTab {...tabProps} />
      )}

      <div ref={floats} className={"ad-floats" + (spot.up ? " up" : "") + (spot.solo ? " solo" : "")}
        style={spot.up && spot.under !== null ? { top: spot.under } : undefined}>
        {toast && (
          <div className="ad-toast" role="status" onClick={() => setToast(null)}>
            <span className="ad-toast-text">{toast}</span>
          </div>
        )}
        {undo && (
          <div className="ad-undo" role="status" key={undo.id}>
            <span className="ad-undo-text">{undo.text}</span>
            <button type="button" className="btn ad-undo-btn" onClick={takeBack}>Undo</button>
            <span className="ad-undo-time" aria-hidden="true" />
          </div>
        )}
      </div>

      {!stopped && !locked && (
        <nav className="ad-tabs" aria-label="Admin sections">
          {TABS.map((x) => (
            <button key={x.id} type="button" className={"ad-tabbtn" + (tab === x.id ? " on" : "")}
              aria-current={tab === x.id ? "page" : undefined} onClick={() => pick(x.id)}>
              {x.label}
            </button>
          ))}
        </nav>
      )}
    </div>
  );
}
